import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {loadScript} from '@paypal/paypal-js';
import {ConfigService, NotificationService} from '../../../../core/services';
import {UntypedFormGroup} from '@angular/forms';
import {ApiService} from '../../../../api/service/api.service';
import {PaymentStep} from '../../models/payment-step';
import {PaymentItemType} from '../../models/payment-type';

@Component({
  selector: 'tj-payment-method',
  templateUrl: './payment-method.component.html',
  styleUrls: ['./payment-method.component.scss'],
})
export class PaymentMethodComponent implements OnInit, OnDestroy {
  @Input() form: UntypedFormGroup;
  @Input() paymentDetails: any;
  @Input() itemType: PaymentItemType;
  @Input() items: any[] = [];
  @Input() paymentMethodIndex: number = 0;
  @Input() vaultId: string;
  @Output('back') goBack = new EventEmitter<void>();
  @Output() paymentSubmit = new EventEmitter<void>();
  @Output() stripePayment = new EventEmitter<void>();
  @Output() paymentStatus = new EventEmitter<{step: PaymentStep; data?: any; error?: any}>();

  readonly PAYMENT_BY_CARD = 0;
  readonly PAYMENT_BY_VAULT = 1;

  isSubmitting = false;
  paypal;
  cardFields;
  isSandbox = true;
  isPaypalInitialized = false;
  initialTabIndex: number;

  // Initialize card fields validation state as invalid
  cardFieldsValid = {
    name: false,
    number: false,
    expiry: false,
    cvv: false,
  };

  private cardFieldsInterval: any;

  constructor(
    private configService: ConfigService,
    private api: ApiService,
    private notificationService: NotificationService,
  ) {
    // Set form as invalid initially
    if (this.form) {
      this.form.setErrors({cardFieldsEmpty: true});
    }
  }

  ngOnInit() {
    // Set form as invalid initially
    if (this.form) {
      this.form.setErrors({cardFieldsEmpty: true});
    }
    this.isSandbox = this.configService.isSandbox();
    this.initialTabIndex = this.paymentMethodIndex;

    // Initialize PayPal if starting on credit card tab
    if (this.paymentMethodIndex === this.PAYMENT_BY_CARD) {
      this.initPaypal();
      this.isPaypalInitialized = true;
    } else if (this.paymentMethodIndex === this.PAYMENT_BY_VAULT) {
      this.initVaultId();
    }
  }

  private initPaypal() {
    const paypalClientId = this.configService.getPaypalClientId(this.isSandbox);
    loadScript({
      clientId: paypalClientId,
      vault: true,
      components: 'buttons,marks,card-fields',
      intent: 'capture',
      currency: 'USD',
    })
      .then(paypal => {
        this.paypal = paypal;
        this.isPaypalInitialized = true;

        if (this.paymentMethodIndex === this.PAYMENT_BY_CARD) {
          const cardField = paypal.CardFields({
            createOrder: () => {
              const order = this.prepareOrder();
              return this.api.payment
                .createPaypalOrder(order, this.isSandbox)
                .toPromise()
                .then(orderData => {
                  if (!orderData || !orderData.id) {
                    throw new Error('Invalid order data received');
                  }
                  return orderData.id as string;
                })
                .catch(error => {
                  this.isSubmitting = false;
                  this.notificationService.error(
                    'Failed to create order: ' + (error.message || 'Unknown error'),
                  );
                  this.paymentStatus.emit({step: PaymentStep.FAILED, error});
                  throw error;
                });
            },
            onApprove: data => {
              const orderId = data.orderID as string;
              return this.api.payment
                .capturePaypalOrder(orderId, this.isSandbox)
                .toPromise()
                .then(orderData => {
                  this.isSubmitting = false;
                  this.paymentStatus.emit({step: PaymentStep.COMPLETED, data: orderData});
                  return orderData;
                })
                .catch(error => {
                  this.isSubmitting = false;
                  this.notificationService.error(
                    'Failed to capture payment: ' + (error.message || 'Unknown error'),
                  );
                  this.paymentStatus.emit({step: PaymentStep.FAILED, error});
                  throw error;
                });
            },
            onError: error => {
              this.isSubmitting = false;
              console.error('PayPal Error:', error);
              this.notificationService.error(
                'Payment failed: ' + (error.message || 'Unknown error'),
              );
              this.paymentStatus.emit({step: PaymentStep.FAILED, error});
            },
          });

          if (cardField.isEligible()) {
            this.clearCardFieldContainers();

            const nameField = cardField.NameField({placeholder: 'Full Name'});
            nameField.render('#card-name-field-container');

            const numberField = cardField.NumberField({placeholder: 'Card Number'});
            numberField.render('#card-number-field-container');

            const cvvField = cardField.CVVField({placeholder: 'CVV'});
            cvvField.render('#card-cvv-field-container');

            const expiryField = cardField.ExpiryField({placeholder: 'MM/YY'});
            expiryField.render('#card-expiry-field-container');

            // Check card fields state periodically
            this.cardFieldsInterval = setInterval(() => {
              if (cardField.isEligible()) {
                cardField.getState().then(state => {
                  const hasEmptyFields =
                    !state.fields?.cardNameField?.isValid ||
                    !state.fields?.cardNumberField?.isValid ||
                    !state.fields?.cardExpiryField?.isValid ||
                    !state.fields?.cardCvvField?.isValid;
                  if (this.form) {
                    if (hasEmptyFields) {
                      this.form.setErrors({cardFieldsEmpty: true});
                    } else {
                      this.form.setErrors(null);
                    }
                  }
                });
              }
            }, 500);
          }
          this.cardFields = cardField;
        }
      })
      .catch(error => {
        console.error('Failed to load PayPal SDK:', error);
        this.notificationService.error('Failed to initialize payment system');
        this.paymentStatus.emit({step: PaymentStep.FAILED, error});
      });
  }

  private prepareOrder() {
    const data = this.form.getRawValue();
    if (!data || !this.paymentDetails) {
      throw new Error('Invalid form data');
    }

    const order = {
      order: [
        {
          itemType: data.itemType,
          items: data.items || [],
          description: data.description || '',
          customerName: data.customerName || '',
          address: data.address || '',
          netAmount: this.paymentDetails.netAmount || 0,
          total: this.paymentDetails.total || 0,
          applyTax: this.paymentDetails.applyTax || false,
          applyCCFee: this.paymentDetails.applyCCFee || false,
          taxRate: this.paymentDetails.taxRate || 0,
          taxFee: this.paymentDetails.taxFee || 0,
          ccFeeRate: this.paymentDetails.ccFeeRate || 0,
          ccFee: this.paymentDetails.ccFee || 0,
        },
      ],
      email: data.email || '',
      savePaymentMethod: data.saveCard || false,
    };

    // Validate required fields
    if (!order.order[0].netAmount || order.order[0].netAmount <= 0) {
      throw new Error('Invalid amount');
    }

    return order;
  }

  private clearCardFieldContainers() {
    const containers = [
      'card-name-field-container',
      'card-number-field-container',
      'card-cvv-field-container',
      'card-expiry-field-container',
    ];

    containers.forEach(id => {
      const container = document.getElementById(id);
      if (container) {
        container.innerHTML = '';
      }
    });
  }

  onSubmit() {
    if (this.paymentMethodIndex === this.PAYMENT_BY_CARD) {
      this.onSubmitByCard();
    } else {
      this.onSubmitByVaultId();
    }
  }

  onSubmitByCard() {
    if (!this.form.valid) {
      this.notificationService.error('Please fill in all required fields');
      return;
    }
    if (!this.cardFields) {
      this.notificationService.error('Payment system not initialized');
      return;
    }
    this.isSubmitting = true;
    this.cardFields.submit().catch(error => {
      this.isSubmitting = false;
      this.notificationService.error('Payment failed: ' + (error.message || 'Unknown error'));
    });
  }

  onSubmitByVaultId() {
    if (!this.vaultId || !this.vaultId.trim()) {
      this.notificationService.error('Vault ID is required');
      return;
    }
    
    // Check if net amount is valid for vault ID payments
    if (!this.paymentDetails.netAmount || this.paymentDetails.netAmount <= 0) {
      this.notificationService.error('Net amount must be greater than 0');
      return;
    }

    this.isSubmitting = true;
    const order = this.prepareOrder();
    this.api.payment
      .createPaypalOrder(
        {
          ...order,
          vaultId: this.vaultId,
        },
        this.isSandbox,
      )
      .toPromise()
      .then(orderData => {
        this.paymentStatus.emit({step: PaymentStep.COMPLETED, data: orderData});
        return orderData;
      })
      .catch(error => {
        this.isSubmitting = false;
        this.notificationService.error('Payment failed: ' + (error.message || 'Unknown error'));
        this.paymentStatus.emit({step: PaymentStep.FAILED, error});
      });
  }

  onStripePayment() {
    this.stripePayment.emit();
  }

  onBack() {
    window.history.back();
  }

  initPayByCreditCard() {
    this.paymentMethodIndex = this.PAYMENT_BY_CARD;
    this.initPaypal();
  }

  initVaultId() {
    if (this.form?.errors?.['cardFieldsEmpty']) {
      const {cardFieldsEmpty, ...otherErrors} = this.form.errors;
      this.form.setErrors(Object.keys(otherErrors).length > 0 ? otherErrors : null);
    }
  }

  onBackToPayment() {
    this.goBack.emit();
  }

  onTabChange(event: any) {
    if (event.index === this.PAYMENT_BY_CARD && !this.isPaypalInitialized) {
      this.initPaypal();
      this.isPaypalInitialized = true;
    }
    
    // Clear form errors when switching to vault ID tab since it doesn't use card fields
    // if (event.index === this.PAYMENT_BY_VAULT && this.form) {
    //   this.form.setErrors(null);
    // }
  }


  private updateFormValidity() {
    const allFieldsValid = Object.values(this.cardFieldsValid).every(valid => valid);
    if (this.form) {
      if (allFieldsValid) {
        this.form.setErrors(null);
      } else {
        this.form.setErrors({cardFieldsInvalid: true});
      }
    }
  }

  ngOnDestroy() {
    // Clear the interval for card fields validation
    if (this.cardFieldsInterval) {
      clearInterval(this.cardFieldsInterval);
    }

    // Clear PayPal card fields
    if (this.cardFields) {
      this.clearCardFieldContainers();
      this.cardFields = null;
    }

    // Clear PayPal instance
    this.paypal = null;
  }
}
